import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Kpi, PageHead } from "@/components/Bits";
import { RemindersCard } from "@/components/RemindersCard";
import { TargetSummary } from "@/components/Progress";
import { ServiceTargets } from "@/components/ServiceTargets";
import { ScoreBreakdown } from "@/components/ScoreBreakdown";
import { ROLE_LABELS } from "@/lib/rbac";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SUB_TINT = {
  active: "bg-emerald-50 text-emerald-700",
  trial: "bg-amber-50 text-amber-700",
  inactive: "bg-rose-50 text-rose-700",
};

export default function StudioDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [target, setTarget] = useState({ revenue_target: "" });
  const isAdmin = user.role === "network_admin";

  const load = () =>
    api.get(`/studios/${id}`).then(({ data }) => {
      setData(data);
      setTarget({
        revenue_target: String(data.scorecard?.revenue_target || ""),
      });
    });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setSub = async (v) => {
    try {
      const { data: s } = await api.put(`/studios/${id}/subscription`, { subscription_status: v });
      setData((d) => ({ ...d, studio: s }));
      toast.success(`Subscription set to ${v}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveTarget = async () => {
    try {
      await api.put("/targets", {
        studio_id: id,
        revenue_target: Number(target.revenue_target || 0),
      });
      toast.success("Monthly target saved");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const actReminder = async (r) => {
    await api.post(`/reminders/${r.id}/act`);
    load();
  };

  if (!data) return <p className="text-slate-500">Loading…</p>;
  const { studio, stats, team, scorecard, profit_trend = [], stock = [], reminders = [], detailers = [], b2b_prospect = null } = data;
  const lowStock = stock.filter((s) => s.quantity <= s.low_threshold);

  return (
    <div>
      <Link to="/studios" className="mb-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-ink">
        <ArrowLeft className="h-4 w-4" /> Back to studios
      </Link>
      <PageHead title={studio.name} subtitle={`${studio.city} · ${studio.address || ""}`}>
        <Badge
          data-testid="studio-subscription-badge"
          className={`border-0 capitalize ${SUB_TINT[studio.subscription_status] || "bg-slate-100"}`}
        >
          {studio.subscription_status}
        </Badge>
      </PageHead>

      {b2b_prospect && (
        <div
          data-testid="studio-b2b-link"
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-brand-line bg-brand-tint/50 p-4 text-sm"
        >
          <div>
            <span className="text-xs uppercase tracking-wide text-slate-500">Linked B2B detailer</span>
            <p className="font-medium text-brand-ink">
              {b2b_prospect.business_name}
              {b2b_prospect.owner_name ? ` · ${b2b_prospect.owner_name}` : ""}
              {b2b_prospect.stage ? ` · ${b2b_prospect.stage}` : ""}
            </p>
          </div>
          <Link to="/b2b" className="font-semibold text-brand-ink underline" data-testid="studio-b2b-open">
            Open in B2B pipeline
          </Link>
        </div>
      )}

      {stats.low_stock > 0 && (
        <div
          data-testid="studio-low-stock-alert"
          className="mb-6 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        >
          <AlertTriangle className="h-4 w-4" strokeWidth={1.8} />
          {stats.low_stock} SKU(s) at or below reorder threshold: {lowStock.map((s) => s.name).join(", ")}
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label="Customers" value={stats.customers} testid="studio-stat-customers" />
        <Kpi label="Jobs" value={stats.jobs} testid="studio-stat-jobs" />
        <Kpi label="B2C revenue (mo)" value={money(scorecard?.month_revenue)} testid="studio-stat-revenue" />
        <Kpi
          label="B2C profit (mo)"
          value={money(scorecard?.month_profit)}
          testid="studio-stat-profit"
          accent="text-emerald-600"
        />
        <Kpi
          label="Tier readiness"
          value={(scorecard?.tier_readiness ?? 0).toFixed(1)}
          hint={`Margin ${scorecard?.margin_pct}%`}
          testid="studio-tier-score"
          accent="text-crimson-600"
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="border-brand-line shadow-none lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">
            Studio B2C performance — target vs actual ({scorecard?.month})
          </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <TargetSummary card={scorecard} />
            <div className="border-t border-brand-line pt-4">
              <ServiceTargets
                studioId={id}
                rows={scorecard?.service_targets || []}
                overall={scorecard?.target_achievement_pct || 0}
                canEdit={isAdmin}
                onSaved={load}
              />
            </div>
            {isAdmin && (
              <div className="flex flex-wrap items-end gap-3 border-t border-brand-line pt-4">
                <div>
                  <Label>Revenue target (₹)</Label>
                  <Input
                    className="mt-1.5 w-40"
                    data-testid="target-revenue-input"
                    value={target.revenue_target}
                    onChange={(e) => setTarget((t) => ({ ...t, revenue_target: e.target.value }))}
                  />
                </div>
                <Button data-testid="save-target-btn" onClick={saveTarget}>
                  Save target
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {isAdmin ? (
              <Select value={studio.subscription_status} onValueChange={setSub}>
                <SelectTrigger data-testid="studio-subscription-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["active", "trial", "inactive"].map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="font-semibold capitalize">{studio.subscription_status}</p>
            )}
            <p className="text-xs text-slate-500">Phone: {studio.phone || "—"}</p>
            <p className="text-xs text-slate-500" data-testid="studio-gst-percent">
              GST rate: {studio.gst_percent ?? 18}%
            </p>
            <p className="text-xs text-slate-500">
              Pending reminders: {scorecard?.pending_reminders ?? 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <ScoreBreakdown card={scorecard} />
      </div>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Studio B2C profit trend</CardTitle>
        </CardHeader>
        <CardContent className="h-72 min-h-[288px] w-full" data-testid="studio-profit-trend">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={profit_trend}>
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={12} stroke="#64748b" />
              <YAxis fontSize={12} stroke="#64748b" tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="revenue" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
              <Bar dataKey="profit" fill="#0f172a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="mt-6">
        <RemindersCard reminders={reminders} onAct={isAdmin ? actReminder : undefined} />

      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card className="border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">PPF Installer assignments</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table data-testid="studio-detailers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>PPF Installer</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Home base</TableHead>
                  <TableHead className="text-right">Jobs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailers.map((d) => (
                  <TableRow key={d.id} className="hover:bg-brand-tint/60">
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell className="text-slate-500">{(d.skills || []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-slate-500">{d.home_base || "—"}</TableCell>
                    <TableCell className="text-right">{d.assigned_jobs}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Stock status</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table data-testid="studio-stock-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stock.map((s) => (
                  <TableRow key={s.id} className="hover:bg-brand-tint/60">
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-right">
                      {s.quantity.toFixed(1)} {s.unit}
                      {s.quantity <= s.low_threshold && (
                        <Badge className="ml-2 border-0 bg-amber-50 text-amber-700">Low</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{money(s.quantity * s.unit_cost)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Team</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="studio-team-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {team.map((u) => (
                <TableRow key={u.id} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell>
                    <Badge className="border border-brand-line bg-slate-50 text-slate-700" variant="outline">
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
